/* =========================================================================
   TOPSPYN — Global behavior
   Vanilla ES module. Loaded with type="module" defer from theme.liquid.

   Controllers wired here:
     • CartDrawer       — open/close, focus trap, escape, scroll lock
     • StickyAtc        — observes [data-sticky-atc-anchor], toggles bar visibility
     • Accordion        — keyboard-accessible <details>-style behavior
     • HeaderScroll     — adds .is-scrolled to <html> past a threshold
     • HeaderHeightVar  — keeps --header-height CSS var in sync

   Cart data flows through Shopify's AJAX cart API (/cart/add.js, /cart.js,
   /cart/change.js, /cart/update.js). Never localStorage.
   ========================================================================= */

const ROOT = document.documentElement;
const focusableSelector =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* ---------------------------------------------------------------------------
   Cart drawer controller — open/close + AJAX cart operations.

   Flow:
     • A user submits any [data-type="add-to-cart-form"] → we POST to
       /cart/add.js, then refresh the drawer via Sections API, then open it.
     • Inside the drawer, [data-cart-change] buttons fire /cart/change.js with
       a delta, and [data-cart-remove] sets quantity to 0.
     • After every cart-mutating request we re-fetch the drawer section HTML
       (so the line items, subtotal, and free-shipping progress all stay
       in sync) and update the header cart-count badge.
   --------------------------------------------------------------------------- */
class CartDrawer {
  constructor(el) {
    this.el = el;
    this.lastFocused = null;
    this.bind();
  }

  /* Re-query inner elements every time since the drawer's HTML is replaced
     after each cart mutation. */
  get panel()   { return this.el.querySelector('[data-cart-drawer-panel]'); }
  get overlay() { return this.el.querySelector('[data-cart-drawer-overlay]'); }
  get closeBtn() { return this.el.querySelector('[data-cart-drawer-close]'); }

  bind() {
    /* Open via [data-cart-trigger]. */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('button[data-cart-trigger], a[data-cart-trigger]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
    });

    /* Close via overlay / close button (event delegation since drawer
       contents re-render after every cart change). */
    this.el.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-close]')) this.close();
      else if (e.target === this.overlay) this.close();
    });

    /* Quantity +/- inside drawer. */
    this.el.addEventListener('click', (e) => {
      const changeBtn = e.target.closest('[data-cart-change]');
      if (changeBtn) {
        const key = changeBtn.dataset.cartLineKey;
        const delta = parseInt(changeBtn.dataset.cartDelta, 10);
        this.changeLine(key, delta);
        return;
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        const key = removeBtn.dataset.cartLineKey;
        this.removeLine(key);
      }
    });

    /* Intercept product form submits → AJAX add to cart, open drawer. */
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-type="add-to-cart-form"], form.sticky-atc__form');
      if (!form) return;
      e.preventDefault();
      this.addToCart(form);
    });

    /* Keyboard. */
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trapFocus(e);
    });
  }

  isOpen() {
    return this.el.getAttribute('aria-hidden') === 'false';
  }

  open() {
    this.lastFocused = document.activeElement;
    this.el.setAttribute('aria-hidden', 'false');
    ROOT.classList.add('is-scroll-locked');
    requestAnimationFrame(() => {
      const first = this.panel?.querySelector(focusableSelector);
      first?.focus();
    });
  }

  close() {
    this.el.setAttribute('aria-hidden', 'true');
    ROOT.classList.remove('is-scroll-locked');
    this.lastFocused?.focus?.();
  }

  trapFocus(e) {
    const focusables = Array.from(
      this.panel?.querySelectorAll(focusableSelector) ?? []
    ).filter((n) => n.offsetParent !== null);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- AJAX cart operations ---------- */

  setLoading(loading) {
    if (loading) this.el.setAttribute('data-loading', '');
    else this.el.removeAttribute('data-loading');
  }

  async addToCart(form) {
    this.setLoading(true);
    this.clearError();
    const submitBtn = form.querySelector('[type="submit"]');
    const origLabel = submitBtn?.innerHTML;
    if (submitBtn) {
      submitBtn.disabled = true;
      if (submitBtn.querySelector('[data-submit-label]')) {
        submitBtn.querySelector('[data-submit-label]').textContent = 'Adding…';
      }
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart.');
      }
      await this.refreshDrawer();
      this.open();
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.setLoading(false);
      if (submitBtn) {
        submitBtn.disabled = false;
        if (origLabel != null) submitBtn.innerHTML = origLabel;
      }
    }
  }

  async changeLine(lineKey, delta) {
    this.setLoading(true);
    this.clearError();
    try {
      /* Read current qty from the rendered DOM (avoids extra round-trip). */
      const item = this.el.querySelector(`[data-cart-line-key="${CSS.escape(lineKey)}"]`);
      const numEl = item?.querySelector('.cart-drawer__num');
      const current = numEl ? parseInt(numEl.textContent, 10) : 1;
      const next = Math.max(0, current + delta);
      await this.postChange(lineKey, next);
      await this.refreshDrawer();
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.setLoading(false);
    }
  }

  async removeLine(lineKey) {
    this.setLoading(true);
    this.clearError();
    try {
      await this.postChange(lineKey, 0);
      await this.refreshDrawer();
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.setLoading(false);
    }
  }

  async postChange(lineKey, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Cart update failed.');
    }
    return res.json();
  }

  /* Replace the cart drawer's outer HTML with a freshly-rendered version
     (via Shopify's Sections API). Also updates the header cart count. */
  async refreshDrawer() {
    const res = await fetch('/?sections=cart-drawer', {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error('Could not refresh cart drawer.');
    const data = await res.json();
    const html = data['cart-drawer'];
    if (!html) return;

    /* Parse the returned HTML, find the .cart-drawer root, and swap our
       drawer's inner contents. We preserve the outer wrapper + aria state
       so the drawer stays open during the swap. */
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-drawer]');
    if (!fresh) return;
    /* Update children only — keep wrapper attributes for open state. */
    this.el.innerHTML = fresh.innerHTML;

    /* Update the header cart count and ping cart.js for the official total. */
    await this.syncHeaderCount();
  }

  async syncHeaderCount() {
    const countEl = document.querySelector('[data-cart-count]');
    if (!countEl) return;
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const n = cart.item_count;
      countEl.textContent = n;
      countEl.classList.toggle('is-empty', n === 0);
    } catch {
      /* Non-critical — header count just stays stale */
    }
  }

  showError(message) {
    this.clearError();
    const errEl = document.createElement('p');
    errEl.className = 'cart-drawer__error';
    errEl.setAttribute('role', 'alert');
    errEl.textContent = message;
    const body = this.el.querySelector('.cart-drawer__body');
    body?.parentNode?.insertBefore(errEl, body);
  }

  clearError() {
    this.el.querySelectorAll('.cart-drawer__error').forEach((n) => n.remove());
  }
}

/* ---------------------------------------------------------------------------
   Sticky ATC — when the page-level [data-sticky-atc-anchor] (usually the
   primary product-form ATC) scrolls out of view, the persistent
   [data-sticky-atc-bar] fades in. Full styling in Phase 3.
   --------------------------------------------------------------------------- */
class StickyAtc {
  constructor(bar, anchor) {
    this.bar = bar;
    this.anchor = anchor;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.toggleAttribute('data-visible', shouldShow);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(this.anchor);
  }
}

/* ---------------------------------------------------------------------------
   Accordion — works on any [data-accordion] with [data-accordion-trigger]
   buttons whose aria-controls points at a [data-accordion-panel].
   --------------------------------------------------------------------------- */
class Accordion {
  constructor(el) {
    this.el = el;
    this.singleOpen = el.hasAttribute('data-accordion-single');
    this.triggers = Array.from(el.querySelectorAll('[data-accordion-trigger]'));
    this.triggers.forEach((t) => {
      t.addEventListener('click', () => this.toggle(t));
      t.addEventListener('keydown', (e) => this.onKey(e, t));
    });
  }

  toggle(trigger) {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    if (this.singleOpen) this.triggers.forEach((t) => this.setOpen(t, false));
    this.setOpen(trigger, !expanded);
  }

  setOpen(trigger, open) {
    trigger.setAttribute('aria-expanded', String(open));
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    panel.hidden = !open;
  }

  /* Arrow keys move focus between triggers; Home/End jump to ends.
     Matches WAI-ARIA Authoring Practices for accordion patterns. */
  onKey(e, trigger) {
    const idx = this.triggers.indexOf(trigger);
    if (idx < 0) return;
    let next = -1;
    switch (e.key) {
      case 'ArrowDown': next = (idx + 1) % this.triggers.length; break;
      case 'ArrowUp':   next = (idx - 1 + this.triggers.length) % this.triggers.length; break;
      case 'Home':      next = 0; break;
      case 'End':       next = this.triggers.length - 1; break;
      default: return;
    }
    e.preventDefault();
    this.triggers[next]?.focus();
  }
}

/* ---------------------------------------------------------------------------
   Header — adds .is-scrolled to <html> when the user scrolls past a
   threshold, used for backdrop blur. Also keeps --header-height in sync.
   --------------------------------------------------------------------------- */
class HeaderScroll {
  constructor(header) {
    this.header = header;
    this.announcement = document.querySelector('[data-announcement-bar]');
    this.threshold = 8;
    this.onScroll = this.onScroll.bind(this);
    this.measure = this.measure.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.measure, { passive: true });
    this.onScroll();
    this.measure();
  }

  onScroll() {
    ROOT.classList.toggle('is-scrolled', window.scrollY > this.threshold);
  }

  measure() {
    const headerH = Math.round(this.header.getBoundingClientRect().height);
    ROOT.style.setProperty('--header-height', `${headerH}px`);
    /* Announcement bar feeds into hero min-height via --announcement-height.
       Resolves to 0 if the bar isn't rendered (toggled off in settings). */
    const announceH = this.announcement
      ? Math.round(this.announcement.getBoundingClientRect().height)
      : 0;
    ROOT.style.setProperty('--announcement-height', `${announceH}px`);
  }
}

/* ---------------------------------------------------------------------------
   Product gallery — clicking a thumbnail swaps the main image.
   Also keyboard navigable via arrow keys when a slide has focus.
   --------------------------------------------------------------------------- */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.slides = Array.from(el.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(el.querySelectorAll('[data-gallery-thumb]'));
    this.thumbs.forEach((t) => {
      t.addEventListener('click', () => this.select(t.dataset.mediaId));
    });
  }
  select(mediaId) {
    this.slides.forEach((s) => {
      const active = s.dataset.mediaId === mediaId;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-pressed', String(active));
    });
    this.thumbs.forEach((t) => {
      const active = t.dataset.mediaId === mediaId;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-current', String(active));
    });
  }
}

/* ---------------------------------------------------------------------------
   Product form — variant switching + quantity stepper.
   Maps the user's option selections to a matching variant, updates the
   hidden id input, refreshes price + availability + button state.
   --------------------------------------------------------------------------- */
class ProductForm {
  constructor(el) {
    this.el = el;
    const variantsScript = el.querySelector('[data-product-variants]');
    try {
      this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    } catch {
      this.variants = [];
    }
    this.variantIdInput = el.querySelector('[data-product-variant-id]');
    this.submitBtn = el.querySelector('[data-product-submit]');
    this.submitLabel = el.querySelector('[data-submit-label]');
    this.submitPrice = el.querySelector('[data-submit-price]');
    this.currentPriceEl = document.querySelector('[data-current-price]');
    this.comparePriceEl = document.querySelector('[data-compare-price]');
    this.stickyVariantInput = document.querySelector('[data-sticky-variant-id]');

    // Wire option changes
    el.querySelectorAll('[data-product-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });

    // Wire quantity stepper
    const qtyInput = el.querySelector('[data-qty-input]');
    el.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const v = Math.max(parseInt(qtyInput.min || '1'), parseInt(qtyInput.value || '1') - 1);
      qtyInput.value = v;
    });
    el.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
      if (!qtyInput) return;
      const max = parseInt(qtyInput.max || '99');
      const v = Math.min(max, parseInt(qtyInput.value || '1') + 1);
      qtyInput.value = v;
    });
    // Clamp a typed value to [min, max] so manual entry can't exceed limits.
    qtyInput?.addEventListener('change', () => {
      const min = parseInt(qtyInput.min || '1');
      const max = parseInt(qtyInput.max || '99');
      let v = parseInt(qtyInput.value, 10);
      if (isNaN(v)) v = min;
      qtyInput.value = Math.min(max, Math.max(min, v));
    });
  }

  onOptionChange() {
    const selected = this.getSelectedOptions();
    const variant = this.variants.find((v) =>
      v.options.every((opt, i) => opt === selected[i])
    );
    if (!variant) {
      this.setUnavailable();
      return;
    }
    if (this.variantIdInput) this.variantIdInput.value = variant.id;
    if (this.stickyVariantInput) this.stickyVariantInput.value = variant.id;
    this.setPrice(variant);
    this.setAvailability(variant);
    this.swapGalleryFor(variant);
    // Update URL without reload for shareability + browser back nav
    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  getSelectedOptions() {
    const opts = [];
    this.el.querySelectorAll('[data-product-option]').forEach((fieldset) => {
      const checked = fieldset.querySelector('[data-product-option-input]:checked');
      if (checked) opts.push(checked.value);
    });
    return opts;
  }

  setPrice(variant) {
    const priceFormatted = this.formatMoney(variant.price);
    if (this.currentPriceEl) this.currentPriceEl.textContent = priceFormatted;
    if (this.submitPrice) this.submitPrice.textContent = priceFormatted;
    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }
  }

  setAvailability(variant) {
    if (!this.submitBtn) return;
    if (variant.available) {
      this.submitBtn.disabled = false;
      this.submitBtn.removeAttribute('aria-disabled');
      if (this.submitLabel) {
        this.submitLabel.innerHTML = `Add to cart — <span data-submit-price>${this.formatMoney(variant.price)}</span>`;
        this.submitPrice = this.submitLabel.querySelector('[data-submit-price]');
      }
    } else {
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      if (this.submitLabel) this.submitLabel.textContent = 'Sold out';
    }
  }

  setUnavailable() {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = true;
    this.submitBtn.setAttribute('aria-disabled', 'true');
    if (this.submitLabel) this.submitLabel.textContent = 'Unavailable';
  }

  swapGalleryFor(variant) {
    if (!variant.featured_media) return;
    const mediaId = String(variant.featured_media.id);
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const slides = gallery.querySelectorAll('[data-gallery-slide]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    slides.forEach((s) => {
      const active = s.dataset.mediaId === mediaId;
      s.classList.toggle('is-active', active);
      s.setAttribute('aria-pressed', String(active));
    });
    thumbs.forEach((t) => {
      const active = t.dataset.mediaId === mediaId;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-current', String(active));
    });
  }

  formatMoney(cents) {
    // Minimal USD formatter; Phase 5 will use Shopify.formatMoney from theme settings
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }
}

/* ---------------------------------------------------------------------------
   Scroll reveals — observes [data-reveal] and [data-reveal-stagger], adds
   [data-revealed] when each enters the viewport. Animation classes in base.css
   transition opacity + translateY.
   --------------------------------------------------------------------------- */
class ScrollReveal {
  constructor() {
    const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (targets.length === 0) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.setAttribute('data-revealed', ''));
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', '');
            this.observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px 15% 0px', threshold: 0 }
    );
    targets.forEach((el) => this.observer.observe(el));
  }
}

/* ---------------------------------------------------------------------------
   Boot — wire everything up after DOMContentLoaded
   --------------------------------------------------------------------------- */
function boot() {
  document.querySelectorAll('[data-cart-drawer]').forEach((el) => new CartDrawer(el));
  document.querySelectorAll('[data-accordion]').forEach((el) => new Accordion(el));
  document.querySelectorAll('[data-product-gallery]').forEach((el) => new ProductGallery(el));
  document.querySelectorAll('[data-product-form]').forEach((el) => new ProductForm(el));

  const header = document.querySelector('[data-site-header]');
  if (header) new HeaderScroll(header);

  const stickyBar = document.querySelector('[data-sticky-atc-bar]');
  const anchor = document.querySelector('[data-sticky-atc-anchor]');
  if (stickyBar && anchor) new StickyAtc(stickyBar, anchor);

  new ScrollReveal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
